import React, { useEffect, useCallback, useMemo } from 'react';
import { Plus, Undo2, Redo2, ExternalLink, Globe } from 'lucide-react';
import { KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import { SavedColorsProvider } from '../context/SavedColorsContext';

import LayoutComponentPropertiesPanel, { canHandleLayoutComponent } from './PageBuilder/LayoutComponentPropertiesPanel';
import SiteDeploymentPanel from './SiteDeploymentPanel';
import { getComponentDefinition } from './componentDefinitions';
import useResponsivePreview from './PageBuilder/hooks/useResponsivePreview';
import useBuilderChrome from './PageBuilder/hooks/useBuilderChrome';
import useBuilderHistory from './PageBuilder/hooks/useBuilderHistory';
import useSelection from './PageBuilder/hooks/useSelection';
import useDocumentSections from './PageBuilder/hooks/useDocumentSections';
import { PageBuilderProvider } from './PageBuilder/context/PageBuilderContext';
import BreakpointToggle, { DESKTOP_CANVAS_WIDTH } from './PageBuilder/BreakpointToggle';
import ZoomControls from './PageBuilder/ZoomControls';
import ConnectedPreviewCanvas from './PageBuilder/PreviewCanvas/ConnectedPreviewCanvas';
import LeftPanel from './PageBuilder/LeftPanel';
import { DEFAULT_COMPONENT_PROPS } from './PageRender/layout/LayoutComponent';
import { TemplateLibraryModal } from './PageBuilder/TemplateLibrary';
import ComponentPicker from './PageBuilder/ComponentPicker';

import ExportSectionModal from './PageBuilder/Modals/ExportSectionModal';
import ImportSectionModal from './PageBuilder/Modals/ImportSectionModal';
import IncompatibleVersionModal from './PageBuilder/Modals/IncompatibleVersionModal';
import MediaLibraryModal from './PageBuilder/Modals/MediaLibraryModal';

import { SECTION_TYPES, createComponentForPalette } from './PageBuilder/utils/builderHelpers';
import SectionEditor from './PageBuilder/SectionEditor';
import QuickPropertiesPanel from './PageBuilder/QuickPropertiesPanel';

// Import custom HTML editor panel component (dynamic require, not a static import)
let CustomHtmlEditorPanel = null;
try {
  const customHtmlModule = require('./PageRender/sections/custom-html/EditorPanel');
  CustomHtmlEditorPanel = customHtmlModule.CustomHtmlEditorPanel;
} catch (err) {
  console.warn('Custom HTML Editor Panel not available:', err);
}


export default function PageBuilder({ page, onSave, saving, onRefreshPage }) {
  const { showSuccess, showError, showInfo } = useToast();
  const toast = useMemo(() => ({ showSuccess, showError, showInfo }), [showSuccess, showError, showInfo]);

  // Selection state (extracted to useSelection hook)
  const selection = useSelection();
  const {
    selectedSection,
    setSelectedSection,
    editingSection,
    setEditingSection,
    resetSelection,
  } = selection;

  // Responsive preview breakpoint state
  const {
    breakpoint,
    setBreakpoint,
    breakpointConfig,
  } = useResponsivePreview({ defaultBreakpoint: 'base' });

  // UI chrome: modals, panels, zoom, placement
  const chrome = useBuilderChrome({ breakpoint });
  const {
    modals,
    setModalState,
    resetModals,
    layersPaletteTarget,
    setLayersPaletteTarget,
    panelWidths,
    isResizing,
    handleMouseDown,
    zoomLevel,
    setZoomLevel,
    setPlacementMode,
    placementModeRef,
    publishing,
    setPublishing,
    siteData,
    setSiteData,
  } = chrome;

  // Undo/Redo history management (wraps useUndoRedo with debounce).
  const {
    historyState,
    canUndo,
    canRedo,
    undo,
    redo,
    reset: resetHistory,
    pushDebounced: debouncedPushToHistory,
    pushImmediate: immediatePushToHistory,
  } = useBuilderHistory();

  // Handler for layers panel "Add" actions via context menu or button
  // Defined before useDocumentSections because addSectionDirectly uses it as onShowComponentPalette
  const handleLayersShowComponentPalette = useCallback((sectionId, parentPath, index) => {
    setLayersPaletteTarget(sectionId ? { sectionId, parentPath, index } : null);
    modals.layersPalette.open();
  }, [setLayersPaletteTarget, modals]);

  // Document data layer: sections, pageConfig, siteSections, CRUD, persistence, handleQuickUpdate
  const doc = useDocumentSections({
    page,
    onSave,
    toast,
    resetHistory,
    setModalState,
    setSelectedSection,
    selectedSection,
    setEditingSection,
    immediatePushToHistory,
    debouncedPushToHistory,
    onShowComponentPalette: handleLayersShowComponentPalette,
    staticSectionTypes: SECTION_TYPES,
  });
  const {
    sections,
    setSections,
    pageConfig,
    allSectionTypes,
    siteSections,
    fetchSections,
    initializeBuilder,
    handleQuickUpdate,
    handleUpdateSection,
    handleDragEnd,
    handleReorderSections,
    handleSaveSection,
    addSectionDirectly,
    addSiteSection,
    insertTemplate,
    handleImportSection,
    handleDeleteSection,
    handleMoveComponent,
    handleSaveColor,
    handleDeleteSavedColor,
  } = doc;

  // Wire history restore → setSections (undo/redo replays sections)
  useEffect(() => {
    if (historyState !== null) {
      setSections(historyState);
    }
  }, [historyState, setSections]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Reset selection state when page changes (e.g., navigating between pages in a site)
  useEffect(() => {
    resetSelection();
    resetModals();
    resetHistory(null);
  }, [page?.id, resetHistory, resetModals, resetSelection]);

  // Keyboard shortcuts for undo/redo and save
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Cmd+S / Ctrl+S — prevent browser save (Page Builder auto-saves)
      if (modKey && e.key === 's') {
        e.preventDefault();
        showInfo('Changes are saved automatically');
        return;
      }

      // Don't trigger other shortcuts when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      if (modKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (modKey && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        redo();
      } else if (!isMac && e.ctrlKey && e.key === 'y') {
        // Windows alternative for redo
        e.preventDefault();
        redo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, showInfo]);

  // Initialize builder on mount / page change
  useEffect(() => {
    initializeBuilder();
  }, [page.id, page.site_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch site data for deployment panel
  useEffect(() => {
    if (page.site_id) {
      apiRequest(`/sites/${page.site_id}`).then(async (res) => {
        if (res.ok) setSiteData(await res.json());
      }).catch(() => {});
    }
  }, [page.site_id, setSiteData]);

  // Show custom HTML editor panel when a custom HTML section is selected
  useEffect(() => {
    const isCustomHtml = selectedSection?.section_type?.startsWith('custom-html');
    setModalState(prev => ({ ...prev, customHtmlEditor: !!isCustomHtml }));
  }, [selectedSection, setModalState]);

  // NOTE: pageConfig sync with page.content is now in useDocumentSections.

  // NOTE: selectedColumn auto-reset on section change is now in useSelection.

  // NOTE: setShowPageSettings / setShowAddSection dropdowns were dead code
  // (only setters were used, state never read). Removed in chrome refactor.

  // NOTE: Zoom keyboard/trackpad, panel resize, and panel resize effects
  // NOTE: fetchSections, fetchSiteSections, createSiteSection, updateSiteSection,
  // and persistSectionContent are now provided by useDocumentSections.

  // Handler to update section metadata (e.g., linking to a site section)

  // NOTE: handleUpdateSection, handleDragEnd, handleReorderSections, handleSaveSection,
  // addSectionDirectly, addSiteSection, insertTemplate, handleImportSection,
  // handleSaveColor, handleDeleteSavedColor, handleDeleteSection, handleMoveComponent
  // are now provided by useDocumentSections (doc).


  // NOTE: updatePageConfig is now provided by useDocumentSections.

  // Handle preview in new tab (publish first if needed)
  const handlePreviewInNewTab = useCallback(() => {
    if (page.is_published) {
      window.open(`${window.location.origin}/pages/${page.id}`, '_blank');
    } else {
      setModalState(prev => ({ ...prev, publishPopover: !prev.publishPopover }));
    }
  }, [page.id, page.is_published, setModalState]);

  const handlePublishAndPreview = useCallback(async () => {
    try {
      setPublishing(true);
      const response = await apiRequest(`/pages/${page.id}/publish`, {
        method: 'POST',
      });
      if (response.ok) {
        showSuccess('Page published successfully');
        if (onRefreshPage) onRefreshPage();
        window.open(`${window.location.origin}/pages/${page.id}`, '_blank');
        setModalState(prev => ({ ...prev, publishPopover: false }));
      } else {
        throw new Error('Failed to publish page');
      }
    } catch (error) {
      console.error('Error publishing page:', error);
      showError('Failed to publish page');
    } finally {
      setPublishing(false);
    }
  }, [page.id, showSuccess, showError, onRefreshPage, setPublishing, setModalState]);




  // Keyboard shortcuts for duplicate (Ctrl/Cmd+D), delete with confirmation (Ctrl/Cmd+Backspace), and force delete (Ctrl/Cmd+Shift+D)
  useEffect(() => {
    const handleKeyboardShortcuts = (e) => {
      // Don't trigger when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;

      const sectionId = selectedSection?.id;
      const componentPath = selectedSection?.content?.selectedComponentPath;

      if (e.key === 'd') {
        // Cmd+D: Duplicate
        e.preventDefault();
        if (sectionId && componentPath) {
          handleQuickUpdate(sectionId, { layoutAction: 'duplicateComponent', componentPath });
        }
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        if (e.shiftKey) {
          // Cmd+Shift+Delete/Backspace: Force delete (no confirmation)
          if (sectionId && componentPath) {
            handleQuickUpdate(sectionId, { layoutAction: 'removeComponent', componentPath });
          } else if (sectionId && !componentPath) {
            handleDeleteSection(sectionId, { skipConfirmation: true });
          }
        } else {
          // Cmd+Delete/Backspace: Delete with confirmation
          if (sectionId && componentPath) {
            if (window.confirm('Delete this component?')) {
              handleQuickUpdate(sectionId, { layoutAction: 'removeComponent', componentPath });
            }
          } else if (sectionId && !componentPath) {
            handleDeleteSection(sectionId);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyboardShortcuts);

    return () => {
      window.removeEventListener('keydown', handleKeyboardShortcuts);
    };
  }, [selectedSection, handleQuickUpdate, handleDeleteSection]);

  // NOTE: placementModeRef sync is now in useBuilderChrome.

  // Handler: placement mode complete (user clicked to place in iframe)
  const handlePlacementComplete = useCallback((sectionId, parentPath, index) => {
    const mode = placementModeRef.current;
    if (!mode) return;
    placementModeRef.current = null; // Synchronous guard against double-click
    // Ensure the target section is selected so the new component gets selected
    const section = sections.find(s => s.id === sectionId);
    if (section && selectedSection?.id !== sectionId) {
      setSelectedSection(section);
    }
    handleQuickUpdate(sectionId, { layoutAction: 'addChild', parentPath, child: mode.componentData, index });
    setPlacementMode(null);
  }, [handleQuickUpdate, sections, selectedSection, placementModeRef, setPlacementMode, setSelectedSection]);

  // NOTE: handlePlacementCancel is now cancelPlacement from useBuilderChrome.
  // handlePlacementCancel is now cancelPlacement directly

  // Handler: section placement mode complete (user clicked to place section in iframe)
  const handleSectionPlacementComplete = async (sectionIndex) => {
    const mode = placementModeRef.current;
    if (!mode) return;
    placementModeRef.current = null; // Synchronous guard against double-click
    setPlacementMode(null);
    await addSectionDirectly('layout-1', { insertAtIndex: sectionIndex });
  };

  // Handler for layers panel drag-and-drop move operations


  // Compose all hook results into a single context value for sub-components
  const builderContext = {
    doc,
    selection,
    history: { canUndo, canRedo, undo, redo, pushImmediate: immediatePushToHistory, pushDebounced: debouncedPushToHistory },
    chrome,
    responsive: { breakpoint, setBreakpoint, breakpointConfig },
    // Page-level props and callbacks
    page,
    onSave,
    onRefreshPage,
    saving,
    sensors,
    // Handler functions (defined in PageBuilder, consumed by children)
    handlers: {
      handleQuickUpdate,
      handleDeleteSection,
      addSectionDirectly,
      handleDragEnd,
      handleReorderSections,
      handleMoveComponent,
      handleUpdateSection,
      handleSaveSection,
      handlePublishAndPreview,
      addSiteSection,
      insertTemplate,
      handleImportSection,
      handleSaveColor,
      handleDeleteSavedColor,
      handleLayersShowComponentPalette,
      handlePlacementComplete,
      handleSectionPlacementComplete,
      handlePreviewInNewTab,
    },
  };

  return (
    <PageBuilderProvider value={builderContext}>
    <SavedColorsProvider
      savedColors={pageConfig.saved_colors || []}
      onSaveColor={handleSaveColor}
      onDeleteSavedColor={handleDeleteSavedColor}
    >
      <style>
        {`
          .section-hover-parent:hover .margin-dragger .dragger-handle {
            opacity: 0.7 !important;
          }
          .section-hover-parent .margin-dragger .dragger-handle:hover {
            opacity: 0.9 !important;
          }
          .section-hover-parent .margin-dragger .dragger-handle.opacity-100 {
            opacity: 1 !important;
          }
        `}
      </style>
      <div className={`w-full h-screen ${isResizing ? 'cursor-col-resize' : ''}`}>
        <div className="bg-white w-full h-full flex flex-col">
          {/* Header */}
     

          {/* Content */}
          <div className="flex-1 flex overflow-hidden resize-container">
            {/* Left Panel - Tabbed Interface */}
            <LeftPanel />

            {/* Left Resize Handle */}
            <div 
              className={`w-1 bg-gray-200 hover:bg-blue-300 cursor-col-resize transition-colors ${isResizing === 'left' ? 'bg-blue-400' : ''}`}
              onMouseDown={() => handleMouseDown('left')}
            ></div>

            {/* Preview Panel */}
            <div
              className="flex flex-col border-r border-gray-200"
              style={{ width: `${panelWidths.center}%` }}
              data-preview-canvas
            >
              {/* Breakpoint Toolbar */}
              <div className="flex items-center justify-center gap-4 py-2 px-4 border-b border-gray-200 bg-white flex-shrink-0">
                <BreakpointToggle
                  activeBreakpoint={breakpoint}
                  onChange={setBreakpoint}
                  size="sm"
                  showWidths={true}
                />
                <span className="text-xs text-gray-400">
                  {breakpoint === 'desktop' ? DESKTOP_CANVAS_WIDTH : breakpointConfig?.width}px
                </span>
                <div className="w-px h-4 bg-gray-300" /> {/* Separator */}
                {/* Undo/Redo Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={undo}
                    disabled={!canUndo}
                    className={`p-1.5 rounded transition-colors ${canUndo ? 'hover:bg-gray-100 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
                    title={navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Undo (⌘Z)' : 'Undo (Ctrl+Z)'}
                  >
                    <Undo2 size={16} />
                  </button>
                  <button
                    onClick={redo}
                    disabled={!canRedo}
                    className={`p-1.5 rounded transition-colors ${canRedo ? 'hover:bg-gray-100 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
                    title={navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Redo (⌘⇧Z)' : 'Redo (Ctrl+Shift+Z)'}
                  >
                    <Redo2 size={16} />
                  </button>
                </div>
                <div className="w-px h-4 bg-gray-300" /> {/* Separator */}
                <ZoomControls
                  zoomLevel={zoomLevel}
                  onZoomChange={setZoomLevel}
                  onFitToWindow={chrome.handleFitToWindow}
                />
                <div className="w-px h-4 bg-gray-300" /> {/* Separator */}
                <button
                  onClick={() => {
                    setLayersPaletteTarget(null);
                    modals.layersPalette.open();
                  }}
                  className="p-1.5 rounded transition-colors hover:bg-gray-100 text-gray-700"
                  title="Add component"
                >
                  <Plus size={16} />
                </button>
                <div className="w-px h-4 bg-gray-300" /> {/* Separator */}
                <div className="relative">
                  <button
                    onClick={handlePreviewInNewTab}
                    className="p-1.5 rounded transition-colors hover:bg-gray-100 text-gray-700"
                    title="Preview in new tab"
                  >
                    <ExternalLink size={16} />
                  </button>
                  {modals.publishPopover.isOpen && (
                    <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
                      <p className="text-sm text-gray-600 mb-3">This page is not published yet.</p>
                      <div className="flex gap-2">
                        <button
                          onClick={handlePublishAndPreview}
                          disabled={publishing}
                          className="flex-1 px-3 py-1.5 bg-brand-600 text-white text-sm rounded-md hover:bg-brand-700 disabled:opacity-50 transition-colors"
                        >
                          {publishing ? 'Publishing...' : 'Publish & Preview'}
                        </button>
                        <button
                          onClick={() => modals.publishPopover.close()}
                          className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {/* Deploy button */}
                {page.site_id && (
                  <div className="relative">
                    <button
                      onClick={() => modals.deployPanel.toggle()}
                      className={`p-1.5 rounded transition-colors hover:bg-gray-100 ${modals.deployPanel.isOpen ? 'bg-gray-100 text-blue-600' : 'text-gray-700'}`}
                      title="Deploy site"
                    >
                      <Globe size={16} />
                    </button>
                    {modals.deployPanel.isOpen && (
                      <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                        <SiteDeploymentPanel
                          site={siteData}
                          siteSections={siteSections}
                          onSiteUpdate={(updated) => setSiteData(updated)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Iframe Preview Canvas — connected via context */}
              <ConnectedPreviewCanvas />
            </div>

            {/* Right Resize Handle */}
            <div 
              className={`w-1 bg-gray-200 hover:bg-blue-300 cursor-col-resize transition-colors ${isResizing === 'right' ? 'bg-blue-400' : ''}`}
              onMouseDown={() => handleMouseDown('right')}
            ></div>

            {/* Properties Panel - Always visible, fixed width */}
            <div
              className="flex-shrink-0"
              style={{ width: `${panelWidths.right}%` }}
            >
              {/* Use new config-driven panel for layout components */}
              {canHandleLayoutComponent(selectedSection) ? (
                <LayoutComponentPropertiesPanel className="h-full" />
              ) : (
                <QuickPropertiesPanel />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Section Editor Modal - Only for editing existing sections */}
      {editingSection && (
        <SectionEditor
          section={editingSection}
          sectionType={editingSection.section_type}
          allSectionTypes={allSectionTypes}
          onSave={handleSaveSection}
          onCancel={() => {
            setEditingSection(null);
          }}
          page={page}
        />
      )}

      {/* Custom HTML Editor Panel - Shows at bottom when custom HTML section is selected */}
      {modals.customHtmlEditor.isOpen && selectedSection && CustomHtmlEditorPanel && (
        <CustomHtmlEditorPanel
          content={selectedSection.content || {}}
          onUpdate={(key, value) => handleQuickUpdate(selectedSection.id, { [key]: value })}
          onClose={() => {
            modals.customHtmlEditor.close();
            setSelectedSection(null);
          }}
        />
      )}

      {/* Layers Panel Component Palette - Modal for adding components from context menu */}
      {modals.layersPalette.isOpen && (() => {
        // All component types available for any parent
        const availableComponents = ['container', 'block', 'spacer', 'row', 'column', 'text', 'image', 'icon', 'button', 'listItem', 'video', 'accordion', 'link'];

        return (
          <ComponentPicker
            onSelectComponent={(componentType) => {
              if (componentType === '_section') {
                setPlacementMode({ componentType: 'section', placementLevel: 'section' });
                modals.layersPalette.close();
                setLayersPaletteTarget(null);
                return;
              }
              if (componentType === '_quickLayout_centeredContent') {
                // Create a section with pre-built container > block structure
                const ts = Date.now();
                const defaultContent = {
                  sectionBackgroundColor: 'transparent',
                  backgroundImage: '',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  marginTop: '0',
                  marginBottom: '16',
                  paddingTop: '0',
                  paddingBottom: '0',
                  paddingLeft: '0',
                  paddingRight: '0',
                  components: [
                    {
                      id: `container-${ts}`,
                      type: 'container',
                      props: { ...DEFAULT_COMPONENT_PROPS.container },
                      children: [
                        {
                          id: `block-${ts}-1`,
                          type: 'block',
                          props: { ...getComponentDefinition('block')?.defaultProps },
                          children: []
                        }
                      ]
                    }
                  ]
                };
                // Use the same section creation pattern as addSectionDirectly but with pre-built content
                (async () => {
                  try {
                    const response = await apiRequest(`/pages/${page.id}/sections`, {
                      method: 'POST',
                      body: JSON.stringify({
                        section_type: 'layout-1',
                        content: defaultContent,
                        order_index: sections.length,
                      }),
                    });
                    if (response.ok) {
                      const newSection = await response.json();
                      showSuccess('Section added successfully');
                      fetchSections();
                      setTimeout(() => { setSelectedSection(newSection); }, 200);
                    } else {
                      showError('Failed to add section');
                    }
                  } catch (error) {
                    console.error('Error adding centered content section:', error);
                    showError('Failed to add section');
                  }
                })();
                modals.layersPalette.close();
                setLayersPaletteTarget(null);
                return;
              }
              const newComponent = createComponentForPalette(componentType);
              if (newComponent) {
                if (layersPaletteTarget) {
                  // Direct insertion at a specific target
                  const { sectionId, parentPath, index } = layersPaletteTarget;
                  handleQuickUpdate(sectionId, {
                    layoutAction: 'addChild',
                    parentPath,
                    child: newComponent,
                    index,
                  });
                } else {
                  // Enter click-to-place mode
                  setPlacementMode({ componentType, componentData: newComponent });
                }
              }
              modals.layersPalette.close();
              setLayersPaletteTarget(null);
            }}
            onClose={() => {
              modals.layersPalette.close();
              setLayersPaletteTarget(null);
            }}
            availableComponents={availableComponents}
            showSectionItem={!layersPaletteTarget}
          />
        );
      })()}

      {/* Template Library Modal */}
      <TemplateLibraryModal
        isOpen={modals.templateLibrary.isOpen}
        onClose={() => modals.templateLibrary.close()}
        onSelectTemplate={(template) => {
          insertTemplate(template);
          modals.templateLibrary.close();
        }}
      />

      {/* Export Section Modal */}
      {modals.exportSection.isOpen && (
        <ExportSectionModal
          isOpen={modals.exportSection.isOpen}
          onClose={() => modals.exportSection.close()}
          section={modals.exportSection.data}
        />
      )}

      {/* Import Section Modal */}
      <ImportSectionModal
        isOpen={modals.importModal.isOpen}
        onClose={() => modals.importModal.close()}
        onImport={handleImportSection}
      />

      {/* Incompatible Version Modal */}
      <IncompatibleVersionModal
        isOpen={modals.incompatibleSections.isOpen}
        onClose={() => modals.incompatibleSections.close()}
        incompatibleSections={modals.incompatibleSections.data}
      />

      {/* Media Library Modal for quick image change */}
      <MediaLibraryModal
        isOpen={modals.mediaLibrary.isOpen}
        onClose={() => modals.mediaLibrary.close()}
        acceptTypes="image"
        currentUrl={selection.selectedComponent?.props?.src || ''}
        onSelect={(media) => {
          // Update the selected image component's src (direct hot path)
          const sectionId = selectedSection?.id;
          const componentPath = selectedSection?.content?.selectedComponentPath;
          if (sectionId && componentPath) {
            doc.updateComponentProp(sectionId, componentPath, { src: media.public_url });
          }
          modals.mediaLibrary.close();
        }}
      />
    </SavedColorsProvider>
    </PageBuilderProvider>
  );
}